import React, {
  createContext,
  useState,
  useContext,
  useEffect,
  ReactNode,
} from "react";

import { PrintOffice } from "../generated/graphql";

interface AuthState {
  token: string;
  printOffice: PrintOffice;
}

interface AuthContextType {
  isAuthenticated: boolean;
  authState: AuthState | null;
  login: (authState: AuthState) => void;
  logout: () => void;
}

// コンテキストの作成
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ローカルストレージのキー
const AUTH_STORAGE_KEY = "print_system_auth";

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // 初期化時にローカルストレージから認証情報を読み込む
  useEffect(() => {
    const loadAuthState = () => {
      const storedAuth = localStorage.getItem(AUTH_STORAGE_KEY);
      if (storedAuth) {
        try {
          const parsedAuth = JSON.parse(storedAuth);
          setAuthState(parsedAuth);
        } catch (e) {
          localStorage.removeItem(AUTH_STORAGE_KEY);
        }
      }
      setIsInitialized(true);
    };

    loadAuthState();
  }, []);

  const login = (newAuthState: AuthState) => {
    setAuthState(newAuthState);
    localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(newAuthState));
  };

  const logout = () => {
    setAuthState(null);
    localStorage.removeItem(AUTH_STORAGE_KEY);
  };

  const value = {
    isAuthenticated: !!authState,
    authState,
    login,
    logout,
  };

  // 初期化が完了するまでローディング表示
  if (!isInitialized) {
    return <div>Loading...</div>;
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
